// PlatformPayouts — admin-only page showing the platform_payouts ledger +
// the "Send to merchant" modal.
//
// Phase 8 step 7. The list refreshes every 30s via usePlatformPayouts so
// status (pending → success/failure) ticks live as the reconciler resolves.
import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Modal, StatusPill } from "@/components/UI";
import { formatDateTime, fmtCcyMinor } from "@/utils/format";
import { useUI } from "@/store/ui";
import { apiErrorMessage } from "@/utils/apiError";
import { providerLabel } from "@/utils/providerLabel";
import { usePlatformPayouts } from "@/queries/usePlatformPayouts";
import { usePlatformBankAccounts } from "@/queries/usePlatformBankAccounts";
import { useMerchants } from "@/queries/useMerchants";
import { listBankAccounts, type BankAccount } from "@/api/bankAccounts";
import {
  initiatePlatformPayout,
  type BuckzyPartyPayload,
} from "@/api/platformPayouts";

export function PlatformPayouts() {
  const [showSend, setShowSend] = useState(false);
  const { data: payouts = [], isLoading, isError } = usePlatformPayouts();
  // AUDIT-PASS-10 FE-L16: load the admin's merchant list so target_merchant_id
  // can be resolved to display_name in the table column. useMerchants is
  // admin-gated and already cached for 60s — cheap to consume here. Lookup
  // falls back to "m_X" if a merchant isn't in the cache (e.g. closed and
  // filtered out elsewhere, but the platform_payouts row still references it).
  const { data: merchants = [] } = useMerchants();
  const merchantNameByID = useMemo(() => {
    const m = new Map<number, string>();
    for (const x of merchants) m.set(x.id, x.display_name);
    return m;
  }, [merchants]);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Console · Platform</div>
          <h1>Platform payouts</h1>
          <div className="sub">
            Admin-initiated transfers from a platform rail to a merchant's
            bank account.
          </div>
        </div>
        <button
          type="button"
          className="btn accent"
          onClick={() => setShowSend(true)}
        >
          <I.Plus size={12} /> Send to merchant
        </button>
      </div>

      <div className="card">
        {isLoading ? (
          <div className="empty">
            <div className="dim" style={{ fontSize: 13 }}>Loading…</div>
          </div>
        ) : isError ? (
          <div className="empty">
            <h3>Couldn't load platform payouts</h3>
            <p>The request to payouts-backend failed.</p>
          </div>
        ) : payouts.length === 0 ? (
          <div className="empty">
            <div className="icon">
              <I.Payouts size={16} />
            </div>
            <h3>No platform payouts yet</h3>
            <p>Send your first transfer to a merchant via the button above.</p>
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>When</th>
                <th>From rail</th>
                <th>To merchant</th>
                <th>Amount</th>
                <th>Mode</th>
                <th>Status</th>
                <th>UTR</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.platform_payout_id}>
                  <td className="dim">{formatDateTime(p.created_at)}</td>
                  <td>pba_{p.platform_bank_account_id}</td>
                  <td>
                    {merchantNameByID.get(p.target_merchant_id) ?? `m_${p.target_merchant_id}`}
                    <span className="dim" style={{ marginLeft: 6, fontSize: 11 }}>
                      · ba_{p.target_bank_account_id}
                    </span>
                  </td>
                  <td>{fmtCcyMinor(p.amount_minor, p.currency)}</td>
                  <td>{p.txn_mode === 1 ? "IMPS" : "NEFT"}</td>
                  <td><StatusPill status={p.status} /></td>
                  <td className="dim">{p.utr_no ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SendToMerchantModal open={showSend} onClose={() => setShowSend(false)} />
    </div>
  );
}

// ─── Send to merchant modal ───────────────────────────────────────────────────

function SendToMerchantModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { addToast } = useUI();
  const { data: rails = [] } = usePlatformBankAccounts();
  // AUDIT-PASS-10 FE-M10: filter to status==='active' so suspended/closed
  // merchants don't appear in the "Send to" dropdown. Original behaviour
  // showed every merchant in the modal; admin could pick a closed merchant
  // and the backend would either reject at dispatch time or (worse) accept
  // and send funds to a soft-closed account that ops shouldn't be using.
  // BE-M5 (target bank_account disabled_at filter, shipped in 2cad6bf) also
  // closes this at the backend boundary — this is the matching UI affordance
  // so the operator sees the constrained set before submit.
  const { data: allMerchants = [] } = useMerchants();
  const merchants = useMemo(
    () => allMerchants.filter((m) => m.status === "active"),
    [allMerchants],
  );

  const [railID, setRailID] = useState<number | "">("");
  const [merchantID, setMerchantID] = useState<number | "">("");
  const [targetBA, setTargetBA] = useState<number | "">("");
  const [targetBankAccounts, setTargetBankAccounts] = useState<BankAccount[]>([]);
  const [beneName, setBeneName] = useState("");
  const [amount, setAmount] = useState("");
  const [txnMode, setTxnMode] = useState<1 | 2>(1);
  const [requestID, setRequestID] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Buckzy-only fields (collected only when the chosen rail is Buckzy AND
  // the target hasn't been registered yet — heuristic: always show when
  // Buckzy is selected; backend returns 400 buckzy_party_required if needed).
  const selectedRail = useMemo(
    () => rails.find((r) => r.platform_bank_account_id === railID),
    [rails, railID],
  );
  const isBuckzyRail = selectedRail?.provider === "buckzy_global_payout";

  // INITIAL_BUCKZY is the canonical empty BuckzyParty form state. Lifted to a
  // const so reset() can restore EVERY field — AUDIT-PASS-10 FE-1 fix. Before
  // this fix, reset() left buckzy state intact across modal opens. Operator
  // pays Acme (fills KYC), closes modal, opens for Beta — Beta's form was
  // pre-filled with Acme's name/address/phone/email. If the operator missed
  // it and clicked Send, Buckzy registered Beta's platform_beneficiary using
  // Acme's identity data. Buckzy caches by external_id → PERMANENT
  // mis-registration without manual data surgery. Cross-tenant data bleed.
  // AUDIT-PASS-10 FE-M11: country_of_operation="IN" and postal_address.country="IN"
  // are Phase-8-scope assumptions — Phase 8 ships INR-only with the platform
  // sending to Indian merchant bank accounts via IMPS/NEFT/UPI (all
  // India-domain RBI clearing channels). When Buckzy global routing is
  // unblocked (cross-border payouts to non-Indian beneficiaries), these
  // hardcodes must become operator-selectable on the form — not silent
  // overrides. The backend's financialInstitution.countryCode is ALSO
  // hardcoded "IN" for the same reason (see BuckzyClient.AddBeneficiary in
  // payouts-backend). Touching one without touching the other regresses
  // the cross-border boundary.
  const INITIAL_BUCKZY = {
    party_type: "B" as "B" | "I",
    legal_name: "",
    first_name: "",
    last_name: "",
    country_of_operation: "IN",
    clearing_channel: "IMPS" as "IMPS" | "NEFT" | "UPI",
    address_line1: "",
    city: "",
    country: "IN",
    postal_code: "",
    mobile_country_code: "91",
    mobile_number: "",
    email_address: "",
  };
  const [buckzy, setBuckzy] = useState(INITIAL_BUCKZY);

  // When merchant changes, fetch its bank-accounts.
  // AUDIT-PASS-10 FE-H2: now catches errors. Previously the .catch was
  // absent; a 403/500 on /v1/merchants/:id/bank-accounts silently produced
  // an empty dropdown — operator thought the merchant had no rails. Now
  // we surface the API error in the modal's error banner.
  useEffect(() => {
    if (typeof merchantID !== "number") {
      setTargetBankAccounts([]);
      setTargetBA("");
      return;
    }
    let cancelled = false;
    void listBankAccounts(merchantID)
      .then((bas) => {
        if (!cancelled) {
          setTargetBankAccounts(bas);
          setTargetBA("");
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setTargetBankAccounts([]);
          setTargetBA("");
          setError(apiErrorMessage(err, "Couldn't load merchant's bank accounts"));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [merchantID]);

  // AUDIT-PASS-10 FE-M10 follow-up: if the merchant the operator selected
  // gets suspended/closed between opens (useMerchants refetches every 60s),
  // the active-only `merchants` filter drops it from the dropdown — but the
  // React state still holds the selected id. The select renders as
  // visually-empty (no matching <option>) but submit would still fire.
  //
  // This effect detects the stale-selection case and clears merchantID +
  // surfaces an error banner so the operator sees what happened. Runs
  // whenever the merchants list changes or merchantID changes; only acts
  // when (merchantID is a number) AND (id isn't in the filtered list).
  useEffect(() => {
    if (typeof merchantID !== "number") return;
    if (merchants.some((m) => m.id === merchantID)) return;
    // Selected merchant dropped out of the active set.
    setMerchantID("");
    setTargetBA("");
    setTargetBankAccounts([]);
    setError("The merchant you selected is no longer active. Pick another.");
  }, [merchants, merchantID]);

  // AUDIT-PASS-10 FE-1: reset() now clears EVERY local state, including the
  // buckzy KYC form and the cached targetBankAccounts list. Without this,
  // a second admin payout to a different merchant could re-use the previous
  // merchant's identity data on a Buckzy registration call.
  function reset() {
    setRailID("");
    setMerchantID("");
    setTargetBA("");
    setTargetBankAccounts([]);
    setBeneName("");
    setAmount("");
    setTxnMode(1);
    setRequestID("");
    setBuckzy(INITIAL_BUCKZY);
    setError(null);
    setSubmitting(false);
    onClose();
  }

  async function handleSubmit() {
    // AUDIT C-5 fix: request_id is no longer a required field. Operators
    // doing one-off platform payouts shouldn't have to invent an idempotency
    // key every time; an empty field gets a UUID auto-generated below.
    // Matches the CSV-v2 batch behaviour (UploadBatchModal.handleSubmit)
    // so the operator's mental model is identical across the two surfaces.
    if (typeof railID !== "number" || typeof targetBA !== "number" || !amount || !beneName) {
      setError("Fill in all required fields");
      return;
    }
    // Resolve the request_id used for THIS submit attempt. Empty → fresh
    // UUID. Kept as a local so the toast on the replay branch can reference
    // whichever value actually went to the backend, not the empty string.
    const effectiveRequestID =
      requestID.trim() !== "" ? requestID.trim() : crypto.randomUUID();
    // Whole-rupees + CLAUDE.md rule 6: no Number()/parseFloat coercions
    // on money. Backend 2747b54 rejects fractional paise with 422
    // amount_not_whole_rupees regardless; tighten here to keep the
    // request from leaving the browser.
    const amountTrim = amount.trim();
    if (!/^\d{1,12}$/.test(amountTrim)) {
      setError(
        "Amount must be a positive whole rupee value — IMPS/NEFT don't settle paise.",
      );
      return;
    }
    const amountMinor = BigInt(amountTrim) * 100n;
    if (amountMinor <= 0n) {
      setError("Amount must be greater than zero.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // AUDIT-PASS-10 FE-H3: strip empty-string optionals at the panel
      // boundary. Buckzy may interpret an empty string ("") as a
      // deliberately-set value (distinct from "field absent"). e.g. an
      // empty `mobile_number` might cause India-resident-detection to
      // skip the mobile-required validation server-side. Coerce empty
      // strings to undefined so JSON.stringify omits the field entirely.
      const undef = (s: string) => (s === "" ? undefined : s);
      const buckzyParty: BuckzyPartyPayload | undefined = isBuckzyRail
        ? {
            party_type: buckzy.party_type,
            legal_name: buckzy.party_type === "B" ? undef(buckzy.legal_name) : undefined,
            first_name: buckzy.party_type === "I" ? undef(buckzy.first_name) : undefined,
            last_name: buckzy.party_type === "I" ? undef(buckzy.last_name) : undefined,
            country_of_operation: undef(buckzy.country_of_operation),
            clearing_channel: buckzy.clearing_channel,
            contact_detail: {
              mobile_country_code: undef(buckzy.mobile_country_code),
              mobile_number: undef(buckzy.mobile_number),
              email_address: undef(buckzy.email_address),
            },
            postal_address: {
              // address_line1, city, country remain REQUIRED per the backend
              // BuckzyPostalAddress shape, so we send them verbatim — an empty
              // one is a bug the operator must fix (caught by client-side
              // validation in handleSubmit's required-fields check below).
              address_line1: buckzy.address_line1,
              city: buckzy.city,
              country: buckzy.country,
              postal_code: undef(buckzy.postal_code),
            },
          }
        : undefined;
      const res = await initiatePlatformPayout({
        platform_bank_account_id: railID,
        target_bank_account_id: targetBA,
        beneficiary_name: beneName,
        amount_minor: amountMinor,
        txn_mode: txnMode,
        request_id: effectiveRequestID,
        buckzy_party: buckzyParty,
      });
      // AUDIT-PASS-10 FE-H4: replay path must be visually distinct from a
      // fresh send. Operator pasting an old request_id by mistake sees a
      // success toast and moves on — would never reconcile the missing
      // payout. The toast API doesn't support "info", so we differentiate
      // via the body copy: replay branch explicitly says "no new payout
      // created", references the existing row, and uses the existing row's
      // status (not the freshly-typed amount which may differ).
      if (res.replayed) {
        addToast({
          title: "Already submitted — no new payout",
          body: `Existing platform_payout_id ${res.platform_payout.platform_payout_id} · status ${res.platform_payout.status}. The original send used request_id "${effectiveRequestID}".`,
          variant: "success",
        });
      } else {
        addToast({
          title: "Platform payout sent",
          body: `${beneName} · ${fmtCcyMinor(res.platform_payout.amount_minor, res.platform_payout.currency)} · ${res.platform_payout.status}`,
          variant: "success",
        });
      }
      void qc.invalidateQueries({ queryKey: ["platform-payouts"] });
      reset();
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't send platform payout"));
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={reset}
      title="Send to merchant"
      wide
      // AUDIT-PASS-10 FE-M8: gate Escape/scrim/X during in-flight submit.
      busy={submitting}
      foot={
        <>
          <button className="btn ghost" type="button" onClick={reset} disabled={submitting}>
            Cancel
          </button>
          <div className="flex-1" />
          <button
            className="btn accent"
            type="button"
            onClick={() => void handleSubmit()}
            disabled={submitting}
          >
            {submitting ? "Sending…" : "Send"}
          </button>
        </>
      }
    >
      <div className="stack-16">
        <div className="field">
          <label className="label">From rail</label>
          <select
            className="input"
            value={railID === "" ? "" : String(railID)}
            onChange={(e) => setRailID(e.target.value === "" ? "" : Number(e.target.value))}
          >
            <option value="">— pick a rail —</option>
            {rails.map((r) => (
              <option key={r.platform_bank_account_id} value={r.platform_bank_account_id}>
                {r.label} · {providerLabel(r.provider)} ({r.environment})
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label">To merchant</label>
          <select
            className="input"
            value={merchantID === "" ? "" : String(merchantID)}
            onChange={(e) => setMerchantID(e.target.value === "" ? "" : Number(e.target.value))}
          >
            <option value="">— pick a merchant —</option>
            {merchants.map((m) => (
              <option key={m.id} value={m.id}>{m.display_name} (#{m.id})</option>
            ))}
          </select>
        </div>

        {typeof merchantID === "number" && (
          <div className="field">
            <label className="label">To bank account</label>
            <select
              className="input"
              value={targetBA === "" ? "" : String(targetBA)}
              onChange={(e) => setTargetBA(e.target.value === "" ? "" : Number(e.target.value))}
            >
              <option value="">— pick a target bank account —</option>
              {targetBankAccounts.map((ba) => (
                <option key={ba.id} value={ba.id}>
                  {ba.label} · {ba.provider} (ba_{ba.id})
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="field">
          <label className="label">Beneficiary name (displayed on payment)</label>
          <input
            className="input"
            type="text"
            value={beneName}
            onChange={(e) => setBeneName(e.target.value)}
            placeholder="Acme Corp"
          />
        </div>

        <div className="row" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1 }}>
            <label className="label">Amount (INR)</label>
            <input
              className="input"
              type="text"
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="1500"
            />
            <div style={{ fontSize: 11, marginTop: 4, color: "var(--ink-3)" }}>
              Whole rupees only — IMPS/NEFT don't settle paise.
            </div>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label className="label">Mode</label>
            <select
              className="input"
              value={txnMode}
              onChange={(e) => setTxnMode(Number(e.target.value) as 1 | 2)}
            >
              <option value="1">IMPS</option>
              <option value="2">NEFT</option>
            </select>
          </div>
        </div>

        <div className="field">
          <label className="label">
            Request ID <span style={{ color: "var(--ink-3)" }}>· optional</span>
          </label>
          <input
            className="input"
            type="text"
            value={requestID}
            onChange={(e) => setRequestID(e.target.value)}
            placeholder="Leave empty to auto-generate a UUID"
          />
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            Idempotency key — submitting the same value again returns the
            existing row (safe to retry on network blips). Leave empty and a
            UUID is generated for you.
          </div>
        </div>

        {isBuckzyRail && (
          <div className="card" style={{ padding: 12 }}>
            <div className="row" style={{ marginBottom: 8 }}>
              <strong style={{ fontSize: 12 }}>Buckzy party data</strong>
              <div className="dim" style={{ fontSize: 11, marginLeft: 8 }}>
                Required on FIRST payout to this merchant via Buckzy. Cached
                after registration — subsequent sends skip this section.
              </div>
            </div>
            <div className="stack-16">
              <div className="field">
                <label className="label">Party type</label>
                <select
                  className="input"
                  value={buckzy.party_type}
                  onChange={(e) => setBuckzy((b) => ({ ...b, party_type: e.target.value as "B" | "I" }))}
                >
                  <option value="B">Business</option>
                  <option value="I">Individual</option>
                </select>
              </div>
              {buckzy.party_type === "B" ? (
                <div className="field">
                  <label className="label">Legal name</label>
                  <input className="input" value={buckzy.legal_name} onChange={(e) => setBuckzy((b) => ({ ...b, legal_name: e.target.value }))} />
                </div>
              ) : (
                <div className="row" style={{ gap: 12 }}>
                  <div className="field" style={{ flex: 1 }}>
                    <label className="label">First name</label>
                    <input className="input" value={buckzy.first_name} onChange={(e) => setBuckzy((b) => ({ ...b, first_name: e.target.value }))} />
                  </div>
                  <div className="field" style={{ flex: 1 }}>
                    <label className="label">Last name</label>
                    <input className="input" value={buckzy.last_name} onChange={(e) => setBuckzy((b) => ({ ...b, last_name: e.target.value }))} />
                  </div>
                </div>
              )}
              <div className="field">
                <label className="label">Address line 1</label>
                <input className="input" value={buckzy.address_line1} onChange={(e) => setBuckzy((b) => ({ ...b, address_line1: e.target.value }))} />
              </div>
              <div className="row" style={{ gap: 12 }}>
                <div className="field" style={{ flex: 1 }}>
                  <label className="label">City</label>
                  <input className="input" value={buckzy.city} onChange={(e) => setBuckzy((b) => ({ ...b, city: e.target.value }))} />
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label className="label">Postal code</label>
                  <input className="input" value={buckzy.postal_code} onChange={(e) => setBuckzy((b) => ({ ...b, postal_code: e.target.value }))} />
                </div>
              </div>
              <div className="row" style={{ gap: 12 }}>
                <div className="field" style={{ flex: 1 }}>
                  <label className="label">Mobile (cc)</label>
                  <input className="input" value={buckzy.mobile_country_code} onChange={(e) => setBuckzy((b) => ({ ...b, mobile_country_code: e.target.value }))} />
                </div>
                <div className="field" style={{ flex: 2 }}>
                  <label className="label">Mobile number</label>
                  <input className="input" value={buckzy.mobile_number} onChange={(e) => setBuckzy((b) => ({ ...b, mobile_number: e.target.value }))} />
                </div>
              </div>
              <div className="field">
                <label className="label">Email</label>
                <input className="input" type="email" value={buckzy.email_address} onChange={(e) => setBuckzy((b) => ({ ...b, email_address: e.target.value }))} />
              </div>
              <div className="field">
                <label className="label">Clearing channel</label>
                <select
                  className="input"
                  value={buckzy.clearing_channel}
                  onChange={(e) => setBuckzy((b) => ({ ...b, clearing_channel: e.target.value as "IMPS" | "NEFT" | "UPI" }))}
                >
                  <option value="IMPS">IMPS</option>
                  <option value="NEFT">NEFT</option>
                  <option value="UPI">UPI</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div style={{ color: "var(--neg)", fontSize: 12 }}>{error}</div>
        )}
      </div>
    </Modal>
  );
}
